// EditedWithdrawal.js
import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { 
  RefreshCw,
  Search,
  Clock,
  Edit2,
  Copy,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  DollarSign,
  Smartphone,
  Tablet,
  Laptop,
  CheckCircle,
  XCircle,
  AlertCircle,
  Wallet
} from 'lucide-react';

const EditedWithdrawal = () => {
  const [editedRequests, setEditedRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  useEffect(() => {
    fetchEditedWithdrawals();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [searchTerm, dateFilter, statusFilter, editedRequests]);

  const fetchEditedWithdrawals = async () => {
    try {
      setLoading(true);
      
      const withdrawalsRef = ref(database, 'withdrawals');
      const withdrawalsSnapshot = await get(withdrawalsRef);
      
      if (!withdrawalsSnapshot.exists()) {
        setEditedRequests([]);
        setFilteredRequests([]);
        setLoading(false);
        return;
      }

      const editedItems = [];
      const data = withdrawalsSnapshot.val();

      // Track statistics
      let today = 0;
      let thisWeek = 0;
      let thisMonth = 0;
      let pending = 0;
      let approved = 0;
      let rejected = 0;
      
      const now = new Date();
      const startOfToday = new Date(now.setHours(0, 0, 0, 0));
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // Iterate through all withdrawals to find edited ones
      Object.entries(data).forEach(([date, users]) => {
        Object.entries(users).forEach(([userId, userRequests]) => {
          Object.entries(userRequests).forEach(([requestId, request]) => {
            // Check if the request was edited (has updatedAt or updatedBy fields)
            if (request.updatedAt || request.updatedBy) {
              const requestData = {
                id: requestId,
                date: date,
                userId: request.userId || userId,
                userName: request.userName || 'Unknown',
                walletType: request.walletType || 'working',
                amount: parseFloat(request.amount) || 0,
                feeAmount: parseFloat(request.feeAmount) || 0,
                netAmount: parseFloat(request.netAmount) || 0,
                crypto: request.crypto || {
                  currency: 'USDT-TRC20',
                  network: 'TRON Network (TRC-20)',
                  address: ''
                },
                status: request.status || 'Pending',
                requestDate: request.requestDate,
                processedDate: request.processedDate || null,
                processedBy: request.processedBy || null,
                updatedAt: request.updatedAt || null,
                updatedBy: request.updatedBy || 'admin',
                withdrawalFee: request.withdrawalFee || 0,
                previousData: request.previousData || null
              };

              editedItems.push(requestData);

              // Calculate stats
              const requestDate = new Date(request.requestDate || date);
              if (requestDate >= startOfToday) today++;
              if (requestDate >= startOfWeek) thisWeek++;
              if (requestDate >= startOfMonth) thisMonth++;
              
              if (request.status === 'Pending') pending++;
              else if (request.status === 'Approved') approved++;
              else if (request.status === 'Rejected') rejected++;
            }
          });
        });
      });

      // Sort by updated date (newest first)
      editedItems.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.requestDate);
        const dateB = new Date(b.updatedAt || b.requestDate);
        return dateB - dateA;
      });

      setEditedRequests(editedItems);
      setFilteredRequests(editedItems);
      setStats({
        total: editedItems.length,
        today,
        thisWeek,
        thisMonth,
        pending,
        approved,
        rejected
      });
    } catch (error) {
      console.error('Error fetching edited withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...editedRequests];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(request => 
        request.userName?.toLowerCase().includes(term) ||
        request.userId?.toLowerCase().includes(term) ||
        request.crypto?.address?.toLowerCase().includes(term) ||
        request.crypto?.currency?.toLowerCase().includes(term) ||
        request.id?.toLowerCase().includes(term)
      );
    }

    // Apply date filter
    if (dateFilter) {
      const filterDate = new Date(dateFilter).toDateString();
      filtered = filtered.filter(request => {
        const requestDate = new Date(request.requestDate).toDateString();
        return requestDate === filterDate;
      });
    }

    // Apply status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Optional: Show toast notification
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Approved':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'Rejected':
        return <XCircle className="h-4 w-4 text-red-400" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-400" />;
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <Edit2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            Edited Withdrawals
          </h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">
            Track all admin edits to withdrawal requests
          </p>
        </div>
        <button
          onClick={fetchEditedWithdrawals}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white text-sm sm:text-base w-full sm:w-auto justify-center"
        >
          <RefreshCw className="h-4 w-4" />
          <span className="sm:inline">Refresh</span>
        </button>
      </div>

      {/* Stats Cards - Expanded for edited withdrawals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="card-glow p-2 sm:p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Edits</div>
          <div className="text-lg sm:text-xl font-bold text-white">{stats.total}</div>
        </div>
        <div className="card-glow p-2 sm:p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Today</div>
          <div className="text-lg sm:text-xl font-bold text-blue-400">{stats.today}</div>
        </div>
        <div className="card-glow p-2 sm:p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">This Week</div>
          <div className="text-lg sm:text-xl font-bold text-green-400">{stats.thisWeek}</div>
        </div>
        <div className="card-glow p-2 sm:p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">This Month</div>
          <div className="text-lg sm:text-xl font-bold text-yellow-400">{stats.thisMonth}</div>
        </div>
        <div className="card-glow p-2 sm:p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Pending</div>
          <div className="text-lg sm:text-xl font-bold text-yellow-400">{stats.pending}</div>
        </div>
        <div className="card-glow p-2 sm:p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Approved</div>
          <div className="text-lg sm:text-xl font-bold text-green-400">{stats.approved}</div>
        </div>
        <div className="card-glow p-2 sm:p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Rejected</div>
          <div className="text-lg sm:text-xl font-bold text-red-400">{stats.rejected}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-glow p-3 sm:p-4 mb-4 sm:mb-6 bg-gray-800/50 rounded-xl">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, ID, currency or address..."
                className="w-full pl-10 pr-4 py-2.5 sm:py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 sm:py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm min-w-[120px]"
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>

            <div className="relative flex-1 sm:flex-none">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full sm:w-auto pl-10 pr-4 py-2.5 sm:py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>
            
            {(searchTerm || dateFilter || statusFilter !== 'All') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('');
                  setStatusFilter('All');
                }}
                className="px-3 py-2.5 sm:py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Table View - With Full Address Display */}
      <div className="hidden sm:block card-glow overflow-hidden mb-6 bg-gray-800/50 rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead className="bg-gray-900/50">
              <tr className="text-sm">
                <th className="py-3 px-4 text-left text-gray-400">Date</th>
                <th className="py-3 px-4 text-left text-gray-400">User</th>
                <th className="py-3 px-4 text-left text-gray-400">Amount</th>
                <th className="py-3 px-4 text-left text-gray-400">Crypto Details</th>
                <th className="py-3 px-4 text-left text-gray-400">Status</th>
                <th className="py-3 px-4 text-left text-gray-400">Edited</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {currentItems.map((request) => (
                <tr key={request.id} className="hover:bg-gray-700/20">
                  <td className="py-3 px-4 text-sm text-gray-300 whitespace-nowrap">
                    {formatShortDate(request.requestDate)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white font-medium">{request.userName}</div>
                    <div className="text-xs text-gray-400">{request.userId}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      <span className={`px-2 py-0.5 rounded ${
                        request.walletType === 'working' ? 'bg-blue-500/20 text-blue-400' :
                        request.walletType === 'roi' ? 'bg-green-500/20 text-green-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        {request.walletType}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white font-bold">{formatCurrency(request.amount)}</div>
                    <div className="text-xs text-gray-400">Fee: {formatCurrency(request.feeAmount)}</div>
                    <div className="text-xs text-green-400">Net: {formatCurrency(request.netAmount)}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      <div className="text-sm text-white font-medium">{request.crypto?.currency}</div>
                      <div className="text-xs text-gray-400">{request.crypto?.network}</div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-300 break-all max-w-[300px]" title={request.crypto?.address}>
                          {request.crypto?.address || 'No address'}
                        </div>
                        {request.crypto?.address && (
                          <button
                            onClick={() => copyToClipboard(request.crypto.address)}
                            className="p-1 hover:bg-gray-700 rounded flex-shrink-0"
                            title="Copy address"
                          >
                            <Copy size={12} className="text-gray-400" />
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(request.status)}
                      <span className={`px-2 py-1 rounded text-xs border ${
                        request.status === 'Pending' 
                          ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                          : request.status === 'Approved'
                          ? 'bg-green-500/20 text-green-400 border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }`}>
                        {request.status}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock size={12} />
                        <span>{formatRelativeTime(request.updatedAt)}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-gray-400">By: </span>
                        <span className="text-primary font-medium">{request.updatedBy}</span>
                      </div>
                      {request.processedDate && request.status !== 'Pending' && (
                        <div className="text-xs text-gray-500">
                          Processed: {formatShortDate(request.processedDate)}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View - With Full Address Display */}
      <div className="block sm:hidden space-y-3">
        {currentItems.map((request) => (
          <div
            key={request.id}
            className="card-glow bg-gray-800/50 rounded-xl p-4 border border-gray-700/30"
          >
            {/* Header with User and Status */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-white font-medium truncate">{request.userName}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1 truncate">{request.userId}</div>
                <div className="mt-2">
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    request.walletType === 'working' ? 'bg-blue-500/20 text-blue-400' :
                    request.walletType === 'roi' ? 'bg-green-500/20 text-green-400' :
                    'bg-purple-500/20 text-purple-400'
                  }`}>
                    {request.walletType}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {getStatusIcon(request.status)}
                <span className={`px-2 py-1 rounded text-xs border ${
                  request.status === 'Pending' 
                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    : request.status === 'Approved'
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                }`}>
                  {request.status}
                </span>
              </div>
            </div>

            {/* Date and Time */}
            <div className="flex items-center gap-1 mb-3 text-xs text-gray-400">
              <Clock size={12} />
              <span>{formatDate(request.requestDate)}</span>
            </div>

            {/* Amount Section */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-gray-900/50 rounded-lg p-2">
                <div className="text-xs text-gray-400">Amount</div>
                <div className="text-sm font-bold text-white">{formatCurrency(request.amount)}</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-2">
                <div className="text-xs text-gray-400">Fee</div>
                <div className="text-sm font-bold text-gray-300">{formatCurrency(request.feeAmount)}</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-2">
                <div className="text-xs text-gray-400">Net</div>
                <div className="text-sm font-bold text-green-400">{formatCurrency(request.netAmount)}</div>
              </div>
            </div>

            {/* Crypto Details - Full Address Display */}
            <div className="mb-3">
              <div className="text-xs text-gray-400 mb-1">Crypto Details:</div>
              <div className="bg-gray-900/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white font-medium">{request.crypto?.currency}</span>
                  <span className="text-xs text-gray-400">{request.crypto?.network}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Wallet className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 break-all text-xs text-gray-300">
                    {request.crypto?.address || 'No address'}
                  </div>
                  {request.crypto?.address && (
                    <button
                      onClick={() => copyToClipboard(request.crypto.address)}
                      className="p-1.5 hover:bg-gray-700 rounded flex-shrink-0"
                      title="Copy address"
                    >
                      <Copy size={14} className="text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Edit Info */}
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-400">Edited</span>
                <span className="text-xs text-primary">{formatRelativeTime(request.updatedAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">By</span>
                <span className="text-xs text-white font-medium">{request.updatedBy}</span>
              </div>
              {request.processedDate && request.status !== 'Pending' && (
                <div className="flex items-center justify-between mt-1 pt-1 border-t border-gray-700/30">
                  <span className="text-xs text-gray-400">Processed</span>
                  <span className="text-xs text-gray-300">{formatShortDate(request.processedDate)}</span>
                </div>
              )}
            </div>

            {/* Expand Button for more details if needed */}
            <button
              onClick={() => setExpandedId(expandedId === request.id ? null : request.id)}
              className="w-full mt-2 text-xs text-primary hover:text-primary/80"
            >
              {expandedId === request.id ? 'Show less' : 'View request ID'}
            </button>

            {/* Expanded Section */}
            {expandedId === request.id && (
              <div className="mt-2 pt-2 border-t border-gray-700/30">
                <div className="text-xs">
                  <span className="text-gray-400">Request ID:</span>
                  <span className="text-white ml-2 break-all">{request.id}</span>
                </div>
                <div className="text-xs mt-1">
                  <span className="text-gray-400">Date Folder:</span>
                  <span className="text-white ml-2">{request.date}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4 sm:mt-6">
          <div className="text-xs sm:text-sm text-gray-400">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredRequests.length)} of {filteredRequests.length} entries
          </div>
          <div className="flex gap-1 sm:gap-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1 
                  ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed' 
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={i}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg text-xs sm:text-sm ${
                      currentPage === pageNum
                        ? 'bg-primary text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages 
                  ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed' 
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* No Results */}
      {filteredRequests.length === 0 && (
        <div className="text-center py-8 sm:py-12 card-glow bg-gray-800/50 rounded-xl">
          <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">📝</div>
          <h3 className="text-base sm:text-lg font-semibold text-white mb-2">No Edited Withdrawals</h3>
          <p className="text-xs sm:text-sm text-gray-400 px-4">
            {searchTerm || dateFilter || statusFilter !== 'All' 
              ? 'No results match your filters' 
              : 'No withdrawal requests have been edited yet'}
          </p>
          {(searchTerm || dateFilter || statusFilter !== 'All') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setDateFilter('');
                setStatusFilter('All');
              }}
              className="mt-4 px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white text-sm"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EditedWithdrawal;